import React from 'react';
import GenericModal from '../../../../ui-components/Modal/GenericModal';
import { GenericTag } from '../../../../ui-components/Tag/GenericTag';
import { SkeletonLine } from '../../../../ui-components/Skeleton/GenericSkeleton';
import { IPv4InfoModalProps } from './BGPModalsData';
import { routeFamilyLabel } from '../BgpVisibilityData';
import { formatCiscoUptime } from '../../../helpers/formattersShapedData';

const IPv4InfoModal: React.FC<IPv4InfoModalProps> = ({ isOpen, onClose, prefixData, neighborIp, neighborInfo }) => {
  const isLoading = isOpen && !prefixData;

  // Format status: first letter uppercase, rest lowercase
  const formatStatus = (status?: string): string => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };
  
  // Get status variant: only ESTABLISHED is success, others are default
  const getStatusVariant = (status?: string): 'success' | 'default' => {
    return status?.toUpperCase() === 'ESTABLISHED' ? 'success' : 'default';
  };

  const status = neighborInfo?.status || 'UNKNOWN';
  const formattedStatus = formatStatus(status);
  const statusVariant = getStatusVariant(status);

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} size="md" title={prefixData ? `IPv4 Prefix Information: ${prefixData.prefix}` : 'IPv4 Prefix Information'}>
      {isLoading ? (
        <div className="space-y-4" role="status" aria-busy="true">
          <div className="space-y-2">
            <SkeletonLine w="35%" h={16} className="rounded" />
            <SkeletonLine w="100%" h={14} className="rounded" />
            <SkeletonLine w="90%" h={14} className="rounded" />
            <SkeletonLine w="70%" h={14} className="rounded" />
          </div>
          <div className="space-y-2">
            <SkeletonLine w="30%" h={16} className="rounded" />
            <SkeletonLine w="100%" h={14} className="rounded" />
            <SkeletonLine w="85%" h={14} className="rounded" />
          </div>
        </div>
      ) : !prefixData ? null : (
      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-semantic-text-primary mb-2">Neighbor Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">Neighbor IP:</span>
              <span className="text-semantic-text-primary font-medium">{neighborIp}</span>
            </div>
            {neighborInfo?.peerAs && (
              <div className="flex justify-between">
                <span className="text-semantic-text-secondary">Peer AS:</span>
                <span className="text-semantic-text-primary font-medium">{neighborInfo.peerAs}</span>
              </div>
            )}
            {neighborInfo?.description && (
              <div className="flex justify-between">
                <span className="text-semantic-text-secondary">Description:</span>
                <span className="text-semantic-text-primary font-medium">{neighborInfo.description}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-semantic-text-secondary">Status:</span>
              <GenericTag
                label={formattedStatus}
                variant={statusVariant}
                size="small"
              />
            </div>
            {neighborInfo?.statusChangedAt && (
              <div className="flex justify-between">
                <span className="text-semantic-text-secondary">Status Changed:</span>
                <span className="text-semantic-text-primary font-medium">{formatCiscoUptime(neighborInfo.statusChangedAt)}</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-semantic-text-primary mb-2">Prefix Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">Prefix:</span>
              <span className="text-semantic-text-primary font-medium">{prefixData.prefix}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">Family:</span>
              <GenericTag
                label={routeFamilyLabel(prefixData.family)}
                variant="neutral"
                size="small"
              />
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-semibold text-semantic-text-primary mb-2">BGP Attributes</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">Local Preference:</span>
              <span className="text-semantic-text-primary font-medium">{prefixData.local_pref}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">MED:</span>
              <span className="text-semantic-text-primary font-medium">{prefixData.med}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">AS_PATH Length:</span>
              <span className="text-semantic-text-primary font-medium">{prefixData.as_path?.length || 0}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-semantic-text-secondary mb-1">AS_PATH:</span>
              <span className="text-semantic-text-primary font-mono text-xs">{prefixData.as_path?.join(' → ') || 'N/A'}</span>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-semibold text-semantic-text-primary mb-2">Propagation</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">VPCs:</span>
              <span className="text-semantic-text-primary font-medium">{prefixData.vpcCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">Route Tables:</span>
              <span className="text-semantic-text-primary font-medium">{prefixData.rtCount}</span>
            </div>
          </div>
        </div>
      </div>
      )}
    </GenericModal>
  );
};

export default IPv4InfoModal;
